import { Capacitor } from "@capacitor/core";

/**
 * Installing the portal as an app ("zum Startbildschirm hinzufügen").
 *
 * Chrome and Edge announce installability with a `beforeinstallprompt` event
 * that fires early — long before a settings page is mounted — so it is
 * captured here at startup and replayed on request. iOS has no such event:
 * Safari installs only through its share sheet, which is why the page shows
 * the steps instead of a button there.
 */

let deferredPrompt = null;
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener(Boolean(deferredPrompt)));

export function captureInstallPrompt() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export function canPromptInstall() {
  return Boolean(deferredPrompt);
}

/** Shows the browser's install dialog; resolves to "accepted" | "dismissed" | null. */
export async function promptInstall() {
  if (!deferredPrompt) return null;
  const event = deferredPrompt;
  deferredPrompt = null;
  event.prompt();
  const choice = await event.userChoice;
  notify();
  return choice?.outcome || null;
}

export function onInstallableChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Running inside the Capacitor shell (the Play Store / App Store build). */
export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
}

/** Installed as an app: the store build, or the PWA opened from the home screen. */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  if (isNativeApp()) return true;
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/** iOS Safari itself — Chrome and Firefox on iOS cannot install web apps. */
export function isIOSSafari() {
  return isIOS() && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
}
